/* eslint-disable react-hooks/exhaustive-deps */
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Flex,
  HStack,
  IconButton,
  Progress,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { useMemo, useRef, useState } from "react";
import ReactSelect from "react-select";

const COLS_OPTIONS = [5, 10, 20, 50, 100, 150];

function simColor(sim) {
  if (sim > 0.85) return "green";
  if (sim > 0.65) return "yellow";
  return "red";
}

function scoreColorScheme(score) {
  if (score >= 1) return "green";
  if (score >= 0.6) return "yellow";
  return "red";
}

function formatSemantic(val) {
  if (!val) return "";
  return val.replace(/\[\d+\]/g, "").replace(/\./g, " → ");
}

function buildSelectStyles(val, isDark) {
  const isUnmapped = val === "" || val === null || val === undefined;
  const borderCol = isUnmapped ? "#FC8181" : "#63B3ED";

  const bg = isDark ? "#2D3748" : "#FFFFFF";
  const optionBg = isDark ? "#2D3748" : "#FFFFFF";
  const optionHoverBg = isDark ? "#4A5568" : "#EBF8FF";
  const textColor = isDark ? "#E2E8F0" : "#1A202C";

  return {
    control: (base, state) => ({
      ...base,
      minHeight: "32px",
      height: "32px",
      fontSize: "12px",
      backgroundColor: bg,
      color: textColor,
      borderColor: borderCol,
      boxShadow: state.isFocused ? `0 0 0 1px ${borderCol}` : "none",
      "&:hover": { borderColor: borderCol },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
      height: "32px",
      flexWrap: "nowrap",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      fontSize: "12px",
      color: textColor,
    }),
    indicatorsContainer: (base) => ({ ...base, height: "32px" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "0 6px",
      color: isDark ? "#A0AEC0" : "#718096",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "12px",
      backgroundColor: state.isSelected
        ? "#63B3ED"
        : state.isFocused
          ? optionHoverBg
          : optionBg,
      color: state.isSelected ? "#FFFFFF" : textColor,
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      fontSize: "12px",
      zIndex: 9999,
      backgroundColor: optionBg,
      position: "absolute",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (base) => ({ ...base, fontSize: "12px", color: "#A0AEC0" }),
    singleValue: (base) => ({ ...base, fontSize: "12px", color: textColor }),
    noOptionsMessage: (base) => ({
      ...base,
      fontSize: "12px",
      color: "#A0AEC0",
    }),
  };
}

export default function MappingTable({
  breakdown,
  mapping,
  importFields,
  onMappingChange,
  requiredFields = [],
}) {
  const [page, setPage] = useState(0);
  const [colsPerPage, setColsPerPage] = useState(10);
  const tableContainerRef = useRef(null);

  const isDark = useColorModeValue(false, true);
  const headerBg = useColorModeValue("gray.50", "gray.800");
  const rowLabelBg = useColorModeValue("gray.50", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("blue.50", "gray.700");
  const footerBg = useColorModeValue("gray.200", "gray.900");
  const textSecondary = useColorModeValue("gray.500", "gray.400");
  const cellBg = useColorModeValue("white", "gray.850");
  const semanticColor = useColorModeValue("blue.400", "blue.300");
  const requiredRowBg = useColorModeValue("red.50", "rgba(254,178,178,0.1)");
  const defaultColBg = useColorModeValue("purple.50", "rgba(128,90,213,0.1)");
  const defaultSemanticColor = useColorModeValue("purple.500", "purple.300");

  // ── Regular cols only — used for pagination ──
  const nonDefaultCols = useMemo(
    () => breakdown.filter((col) => col.csv_header !== "Default"),
    [breakdown],
  );

  // ── Each Default row becomes its OWN column with a unique index-based key ──
  const defaultCols = useMemo(
    () =>
      breakdown
        .filter((col) => col.csv_header === "Default")
        .map((col, i) => ({
          ...col,
          _isDefault: true,
          _defaultKey: `__DEFAULT_${i}__`,
        })),
    [breakdown],
  );

  const total = nonDefaultCols.length;
  const pageCount = Math.ceil(total / colsPerPage);
  const start = page * colsPerPage;
  const end = Math.min(start + colsPerPage, total);

  // Spread all Default cols individually at the end of the last page
  const pageCols = useMemo(() => {
    const slice = nonDefaultCols.slice(start, end);
    const isLastPage = end >= total;
    return isLastPage && defaultCols.length
      ? [...slice, ...defaultCols]
      : slice;
  }, [nonDefaultCols, defaultCols, start, end, total]);

  const options = useMemo(
    () => [
      { value: "", label: "— Not Mapped —" },
      ...importFields.map((f) => ({
        value: f.name,
        label: (f.label || f.name) + (f.required ? " *" : ""),
        isRequired: f.required,
        semantic: f.semantic,
      })),
    ],
    [importFields],
  );

  const COL_W = "220px";
  const LABEL_W = "120px";

  return (
    <Flex direction="column" h="100%" overflow="hidden">
      <Box flex="1" overflow="auto" ref={tableContainerRef}>
        <Table
          size="sm"
          variant="simple"
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
            tableLayout: "fixed",
            minWidth: `calc(${LABEL_W} + ${pageCols.length} * ${COL_W})`,
          }}
        >
          {/* ── HEADER ── */}
          <Thead>
            <Tr>
              <Th
                bg={headerBg}
                position="sticky"
                left={0}
                top={0}
                zIndex={5}
                w={LABEL_W}
                px={4}
                py={4}
                borderBottom="2px solid"
                borderRight="1px solid"
                borderColor={borderColor}
                fontSize="11px"
                textTransform="uppercase"
                color={textSecondary}
              >
                Field
              </Th>

              {pageCols.map((col, colIdx) =>
                col._isDefault ? (
                  <Th
                    key={col._defaultKey}
                    bg={headerBg}
                    position="sticky"
                    top={0}
                    zIndex={4}
                    w={COL_W}
                    px={3}
                    borderBottom="2px solid"
                    borderRight="1px solid"
                    borderColor={borderColor}
                  >
                    <Tooltip label={formatSemantic(col.semantic)}>
                      <Box py={3}>
                        <Text fontSize="12px" fontWeight="600">
                          Default
                        </Text>
                        <Text
                          fontSize="10px"
                          color={textSecondary}
                          noOfLines={1}
                        >
                          {formatSemantic(col.semantic)}
                        </Text>
                      </Box>
                    </Tooltip>
                  </Th>
                ) : (
                  <Th
                    key={col.csv_header}
                    bg={headerBg}
                    position="sticky"
                    top={0}
                    zIndex={4}
                    w={COL_W}
                    px={3}
                    borderBottom="2px solid"
                    borderRight="1px solid"
                    borderColor={borderColor}
                  >
                    <Tooltip label={col.csv_header}>
                      <Box py={3}>
                        <Text fontSize="12px" fontWeight="600">
                          {col.csv_header}
                        </Text>
                        <Text fontSize="10px" color={textSecondary}>
                          col {start + colIdx + 1}
                        </Text>
                      </Box>
                    </Tooltip>
                  </Th>
                ),
              )}
            </Tr>
          </Thead>

          <Tbody>
            {/* ── MAPS TO row ── */}
            <Tr _hover={{ bg: hoverBg }}>
              <Td
                bg={rowLabelBg}
                position="sticky"
                left={0}
                zIndex={2}
                w={LABEL_W}
                px={4}
                py={4}
                borderRight="1px solid"
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                <Text fontSize="11px" fontWeight="600" color={textSecondary}>
                  Maps To
                </Text>
              </Td>

              {pageCols.map((col) => {
                if (col._isDefault) {
                  return (
                    <Td
                      key={col._defaultKey}
                      bg={defaultColBg}
                      px={3}
                      py={3}
                      borderRight="1px solid"
                      borderBottom="1px solid"
                      borderColor={borderColor}
                      w={COL_W}
                    >
                      <Text
                        fontSize="10px"
                        color={defaultSemanticColor}
                        fontFamily="mono"
                        noOfLines={1}
                        title={col.semantic}
                        mb={1}
                      >
                        {formatSemantic(col.semantic)}
                      </Text>
                      <Text
                        fontSize="11px"
                        fontWeight="600"
                        noOfLines={1}
                        title={col.value}
                      >
                        {col.reference ? `ref: ${col.value}` : col.value}
                      </Text>
                      {col.transform && (
                        <Text fontSize="10px" color={textSecondary} mt={0.5}>
                          transform: {col.transform.type}
                        </Text>
                      )}
                    </Td>
                  );
                }

                const currentVal = mapping[col.csv_header] ?? "";
                const selectedOption =
                  options.find((o) => o.value === currentVal) ?? options[0];
                const isRequiredAndUnmapped =
                  !currentVal &&
                  col.matched_column &&
                  requiredFields.includes(col.matched_column);

                return (
                  <Td
                    key={col.csv_header}
                    bg={isRequiredAndUnmapped ? requiredRowBg : cellBg}
                    px={3}
                    py={3}
                    borderRight="1px solid"
                    borderBottom="1px solid"
                    borderColor={borderColor}
                    w={COL_W}
                  >
                    <ReactSelect
                      options={options}
                      value={selectedOption}
                      onChange={(selected) => {
                        onMappingChange(col.csv_header, selected?.value ?? "");
                      }}
                      styles={buildSelectStyles(currentVal, isDark)}
                      isSearchable
                      placeholder="— Not Mapped —"
                      menuPortalTarget={null}
                      menuPosition="absolute"
                      menuPlacement="auto"
                      formatOptionLabel={(option) => (
                        <Box>
                          <Text fontSize="12px">{option.label}</Text>
                          {option.semantic && (
                            <Text fontSize="10px" color="#63B3ED">
                              {formatSemantic(option.semantic)}
                            </Text>
                          )}
                        </Box>
                      )}
                    />
                    {col.semantic && (
                      <Text
                        fontSize="10px"
                        color={semanticColor}
                        mt={1}
                        fontFamily="mono"
                        noOfLines={1}
                        title={col.semantic}
                      >
                        {formatSemantic(col.semantic)}
                      </Text>
                    )}
                  </Td>
                );
              })}
            </Tr>

            {/* ── SIMILARITY row ── */}
            <Tr>
              <Td
                bg={rowLabelBg}
                position="sticky"
                left={0}
                zIndex={2}
                px={4}
                py={4}
                borderRight="1px solid"
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                <Text fontSize="11px" fontWeight="600">
                  Similarity
                </Text>
              </Td>

              {pageCols.map((col) => {
                if (col._isDefault) {
                  return (
                    <Td
                      key={col._defaultKey}
                      px={3}
                      py={4}
                      bg={defaultColBg}
                      borderRight="1px solid"
                      borderBottom="1px solid"
                      borderColor={borderColor}
                    >
                      <Text fontSize="11px" color={textSecondary}>
                        —
                      </Text>
                    </Td>
                  );
                }
                const simPct = Math.round((col.similarity ?? 0) * 100);
                return (
                  <Td
                    key={col.csv_header}
                    px={3}
                    py={4}
                    borderRight="1px solid"
                    borderBottom="1px solid"
                    borderColor={borderColor}
                  >
                    <Flex align="center" gap={2}>
                      <Progress
                        value={simPct}
                        size="sm"
                        flex="1"
                        colorScheme={simColor(col.similarity)}
                      />
                      <Text fontSize="11px">{simPct}%</Text>
                    </Flex>
                  </Td>
                );
              })}
            </Tr>

            {/* ── GRADE row ── */}
            <Tr>
              <Td
                bg={rowLabelBg}
                position="sticky"
                left={0}
                zIndex={2}
                px={4}
                py={4}
                borderRight="1px solid"
                borderColor={borderColor}
              >
                <Text fontSize="11px" fontWeight="600">
                  Grade
                </Text>
              </Td>

              {pageCols.map((col) => {
                if (col._isDefault) {
                  return (
                    <Td
                      key={col._defaultKey}
                      px={3}
                      py={4}
                      bg={defaultColBg}
                      borderRight="1px solid"
                      borderColor={borderColor}
                    >
                      <Text fontSize="11px" color={textSecondary}>
                        —
                      </Text>
                    </Td>
                  );
                }
                return (
                  <Td
                    key={col.csv_header}
                    px={3}
                    py={4}
                    borderRight="1px solid"
                    borderColor={borderColor}
                  >
                    <Badge colorScheme={scoreColorScheme(col.score)}>
                      {col.score ?? "—"}
                    </Badge>
                  </Td>
                );
              })}
            </Tr>
          </Tbody>
        </Table>
      </Box>

      {/* ── FOOTER ── */}
      <Box borderTop="1px solid" borderColor={borderColor} bg={footerBg} p={3}>
        <Flex justify="space-between" align="center">
          <HStack>
            <Text fontSize="13px">Columns</Text>
            <Select
              size="sm"
              value={colsPerPage}
              onChange={(e) => {
                setColsPerPage(Number(e.target.value));
                setPage(0);
              }}
              w="70px"
            >
              {COLS_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Select>
          </HStack>

          <HStack>
            <Text fontSize="13px">
              {start + 1}–{end} of {total}
            </Text>
            <IconButton
              size="xs"
              icon={<ChevronLeftIcon />}
              isDisabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
            />
            <IconButton
              size="xs"
              icon={<ChevronRightIcon />}
              isDisabled={page >= pageCount - 1}
              onClick={() => setPage((p) => p + 1)}
            />
          </HStack>
        </Flex>
      </Box>
    </Flex>
  );
}
